import axios from "axios";

import { AOO_API_HOST } from "../../../../env";
import { PostExpert } from "../../../Widgets/Log/log.typing";
import {
  MachineConfiguration,
  MachineGroupOption,
  MachineQrCode,
  MachineSaveResult,
} from "../machineEditor.typing";

const PRODUCTIONS = `${AOO_API_HOST}/aoo-api/Productions`;
const PRODUCTIONS_LOG = `${AOO_API_HOST}/aoo-api/ProductionsLog`;

/**
 * Manufacturing.API answers a validation failure with 400 and { Messages: [...] }, which the AOO hub
 * passes through untouched. Anything else is a genuine fault and gets a generic message.
 */
export const readApiError = (error: any, fallback: string): string => {
  const messages = error?.response?.data?.Messages;
  if (Array.isArray(messages) && messages.length > 0) {
    return messages.join(" ");
  }

  const message = error?.response?.data?.message;
  if (typeof message === "string" && message.length > 0) {
    return message;
  }

  return fallback;
};

export const fetchMachineGroups = async (): Promise<MachineGroupOption[]> => {
  const response = await axios.get(`${PRODUCTIONS}/machine-groups`);
  return (response.data as MachineGroupOption[]) ?? [];
};

/** The same list the log's user picker offers, so an edit is attributed the same way a post is. */
export const fetchMachineShopUsers = async (): Promise<PostExpert[]> => {
  const response = await axios.get(`${PRODUCTIONS_LOG}/GetWorkers`);
  return (response.data as PostExpert[]) ?? [];
};

export const fetchMachineConfiguration = async (
  machineId: string
): Promise<MachineConfiguration> => {
  const response = await axios.get(`${PRODUCTIONS}/machine-configuration`, {
    params: { machineId },
  });
  return response.data as MachineConfiguration;
};

export const createMachine = async (payload: {
  MachineName: string;
  MachineCode: string;
  GroupId: string | null;
  IsAvailable: boolean;
  EditorId: string;
}): Promise<MachineSaveResult> => {
  const response = await axios.post(`${PRODUCTIONS}/create-machine`, payload);
  return response.data as MachineSaveResult;
};

export const editMachine = async (payload: {
  MachineId: string;
  MachineName: string;
  MachineCode: string;
  GroupId: string | null;
  IsAvailable: boolean;
  EditorId: string;
}): Promise<MachineSaveResult> => {
  const response = await axios.post(`${PRODUCTIONS}/edit-machine`, payload);
  return response.data as MachineSaveResult;
};

export const fetchMachineQrCode = async (
  machineId: string
): Promise<MachineQrCode> => {
  const response = await axios.get(`${PRODUCTIONS}/machine-qr-code`, {
    params: { machineId },
  });
  return response.data as MachineQrCode;
};

/**
 * The read a shop scanner performs after decoding a QR code. Used to confirm a freshly created
 * machine really resolves before its code is offered for printing.
 */
export const verifyMachineIsScannable = async (
  machineId: string
): Promise<boolean> => {
  try {
    const response = await axios.get(`${PRODUCTIONS}/GetMachine`, {
      params: { machineId },
    });

    // A machine that does not exist comes back as an empty DTO with an all-zero id, so compare
    // rather than just checking the call succeeded.
    const returnedId: string | undefined = response.data?.MachineId;
    return (
      typeof returnedId === "string" &&
      returnedId.toLowerCase() === machineId.toLowerCase()
    );
  } catch (error) {
    return false;
  }
};
